import { MailVolume } from '@sharedlib/common/types/mail-app/mail-volume';
import {
  type RailMenuEntry,
  type RailMenuItem,
  type RailRow,
  type RailRowActionId,
  railRowMenu,
} from '@modules/common/components/rail-menu/rail-menu';
import { type VirtualFolder } from '@modules/messages/types/virtual-folder';

/** Mail's rail rows by the prototype's names. Rows not listed here keep their own menu. */
export const MAIL_RAIL_ROWS: Readonly<Partial<Record<VirtualFolder, RailRow>>> = {
  inbox: 'all',
  all_folders: 'all',
  sent: 'sent',
  draft: 'draft',
  scheduled: 'scheduled',
  flagged: 'flagged',
  vip: 'vip',
  followed: 'followup',
  snoozed: 'snoozed',
  following: 'following',
  other_mail: 'other',
  bulk: 'bulk',
  spam: 'spam',
  archived: 'archive',
  deleted: 'trash',
};

const MAIL_LIVE: ReadonlySet<RailRowActionId> = new Set<RailRowActionId>([
  'new-item',
  'new-container',
  'read-all',
  'clear',
  'empty-trash',
  'trash-archive',
  'settings',
]);

export const SEND_ALL_ENTRY: RailMenuEntry = { id: 'send-all', title: 'Send all', icon: 'sapphire-sent' };
export const DELETE_ALL_ENTRY: RailMenuEntry = { id: 'delete-all', title: 'Delete all', icon: 'sapphire-trash' };

export interface MailRowMenuContext {
  /** Why this person may not empty this Trash; absent when they may. */
  readonly purgeRefused?: string;
}

/** A My Workspace mail row's menu; `undefined` for a row the shared definition does not cover. */
export function mailRowMenu(
  folder: VirtualFolder,
  context: MailRowMenuContext = {},
): readonly RailMenuItem[] | undefined {
  const row = MAIL_RAIL_ROWS[folder];
  if (!row) return undefined;

  const live = new Set(MAIL_LIVE);
  // My Workspace has no Following list yet, so there are no subscriptions to clear.
  if (row === 'following') live.delete('clear');

  // Ours, not the prototype's: kept beside it (additive rule).
  const extras = row === 'scheduled' ? [SEND_ALL_ENTRY] : row === 'spam' ? [DELETE_ALL_ENTRY] : [];

  return railRowMenu({
    row,
    itemNoun: 'email',
    containerNoun: 'folder',
    // My Workspace Following lists nothing yet (Coming soon), and the bulk read has no "following" filter: it would read the whole mailbox.
    readable: row !== 'following' && folder !== 'all_folders',
    live,
    refused: context.purgeRefused ? { 'empty-trash': context.purgeRefused } : undefined,
    extras,
  });
}

/** The prototype's sender groups, in its order. */
export const MAIL_VOLUME_GROUPS: ReadonlyArray<{ value: MailVolume; title: string; icon: string; hint: string }> = [
  { value: MailVolume.EVERYONE, title: 'Anyone', icon: 'sapphire-email', hint: 'All senders can reach your Inbox.' },
  { value: MailVolume.VIP, title: 'VIPs', icon: 'sapphire-vip', hint: 'Senders you mark as VIP in Mail or Contacts.' },
  {
    value: MailVolume.CONTACTS,
    title: 'Contacts',
    icon: 'sapphire-groups',
    hint: 'People in this workspace’s address book.',
  },
  {
    value: MailVolume.NETWORK,
    title: 'Network',
    icon: 'sapphire-groups',
    hint: 'Recipients from sent mail and visible To/Cc in earlier received mail.',
  },
];

/** Strictest first. Our levels are cumulative: each one admits every stricter group too. */
const LEVELS: readonly MailVolume[] = [MailVolume.VIP, MailVolume.CONTACTS, MailVolume.NETWORK];

/** A group is ticked when the current level lets it reach the Inbox. */
export function mailVolumeChecked(current: MailVolume, group: MailVolume): boolean {
  if (group === MailVolume.EVERYONE || current === MailVolume.EVERYONE) return group === current;
  return LEVELS.indexOf(group) <= LEVELS.indexOf(current);
}

/**
 * The level after a click. A group that is not the current level becomes it; unticking the current level
 * steps back to the next stricter one, and below VIP to Anyone — the prototype's "nothing left means Anyone".
 */
export function nextMailVolume(current: MailVolume, clicked: MailVolume): MailVolume {
  if (clicked === MailVolume.EVERYONE) return MailVolume.EVERYONE;
  if (!mailVolumeChecked(current, clicked)) return clicked;
  return LEVELS[LEVELS.indexOf(clicked) - 1] ?? MailVolume.EVERYONE;
}

export const MAIL_VOLUME_ENTRY_PREFIX = 'volume:';

/**
 * The My Inbox chevron's menu, also shown when the My Inbox row is right-clicked.
 * `current` is `undefined` only while the saved level has never been loaded: nothing is ticked then,
 * rather than a guessed "Anyone" that the answer would have to take back.
 */
export function mailVolumeMenu(current: MailVolume | undefined): readonly RailMenuItem[] {
  return [
    ...MAIL_VOLUME_GROUPS.map(({ value, title, icon, hint }) => ({
      id: MAIL_VOLUME_ENTRY_PREFIX + value,
      title,
      icon,
      hint,
      checked: current !== undefined && mailVolumeChecked(current, value),
    })),
    null,
    { id: 'open-other', title: 'Open Other Mail', icon: 'sapphire-email' },
    null,
    { id: 'read-all', title: 'Mark all as read for me', icon: 'sapphire-email' },
    { id: 'volume-settings', title: 'Mail volume settings…', icon: 'sapphire-settings' },
  ];
}

/**
 * The personal rail's Inbox menu, for every login (SBUX-1183): the My Inbox menu in the released words. "My Inbox"
 * and "Mark all as read for me" are Teams' names, not yet released; a personal mailbox has one read state.
 */
export function inboxVolumeMenu(current: MailVolume | undefined): readonly RailMenuItem[] {
  return mailVolumeMenu(current).map((entry) =>
    entry?.id === 'read-all' ? { ...entry, title: 'Mark all as read' } : entry,
  );
}

/** The group a volume entry id names; `undefined` for any other entry. */
export function mailVolumeFromEntry(id: string): MailVolume | undefined {
  return MAIL_VOLUME_GROUPS.find(({ value }) => MAIL_VOLUME_ENTRY_PREFIX + value === id)?.value;
}

/** The prototype's save message (mail-volume.mjs:14), as it reads for each scope. */
export const MAIL_VOLUME_SAVED_TEXT = 'Mail volume saved for your My Inbox. Other senders stay in Other Mail.';
export const MAIL_VOLUME_INBOX_SAVED_TEXT = 'Mail volume saved for your Inbox. Other senders stay in Other Mail.';
export const MAIL_VOLUME_TEAM_SAVED_TEXT = 'Mail volume saved for your Team Inbox. Other senders stay in Other Mail.';
/** The same save while the server is still placing the mailbox's existing mail by the new level. */
export const MAIL_VOLUME_TEAM_UPDATING_TEXT = 'Mail volume saved for your Team Inbox. Updating the mail already in it…';

/** Team rows by the prototype's names: the My rows plus the two the team rail alone draws. */
export const TEAM_MAIL_RAIL_ROWS: Readonly<Partial<Record<VirtualFolder, RailRow>>> = {
  ...MAIL_RAIL_ROWS,
  assigned_to_me: 'assigned',
  all_folders: 'all',
};

/** The bulk read sends no narrowing filter for these: it would mark the whole team mailbox read. */
const TEAM_UNREADABLE: ReadonlySet<VirtualFolder> = new Set<VirtualFolder>([
  'assigned_to_me',
  'following',
  'all_folders',
]);

/** The rows whose team menu already offered "Mark all as read" (team-wide): kept (additive rule). */
const TEAM_SHARED_READ: ReadonlySet<VirtualFolder> = new Set<VirtualFolder>([
  'flagged',
  'vip',
  'followed',
  'snoozed',
  'other_mail',
  'bulk',
  'spam',
  'archived',
  'deleted',
]);

export const TEAM_READ_ALL_ENTRY: RailMenuEntry = {
  id: 'read-all-team',
  title: 'Mark all as read',
  icon: 'sapphire-email',
  hint: 'Team mail has one read state: this marks the row read for everyone in the team.',
};

const TEAM_CLEAR_ENTRIES: Partial<Record<VirtualFolder, RailMenuEntry>> = {
  flagged: {
    id: 'clear-team',
    title: 'Unflag all',
    icon: 'sapphire-close',
    hint: 'Team emails have one flag: this removes it for everyone in the team.',
  },
  followed: {
    id: 'clear-team',
    title: 'Remove all follow-ups',
    icon: 'sapphire-close',
    hint: 'Team emails have one follow-up: this clears it for everyone in the team.',
  },
  snoozed: {
    id: 'clear-team',
    title: 'Unsnooze all',
    icon: 'sapphire-close',
    hint: 'Team emails have one snooze: this clears it for everyone in the team.',
  },
};

/** A Team Workspace mail row's menu. Only Follow is per member in the team mailbox. */
export function teamMailRowMenu(
  folder: VirtualFolder,
  context: MailRowMenuContext = {},
): readonly RailMenuItem[] | undefined {
  const row = TEAM_MAIL_RAIL_ROWS[folder];
  if (!row) return undefined;

  const live = new Set<RailRowActionId>(['new-item', 'new-container', 'empty-trash', 'trash-archive', 'settings']);
  if (row === 'following') live.add('clear');
  const clear = TEAM_CLEAR_ENTRIES[folder];
  const extras: RailMenuEntry[] = [
    ...(TEAM_SHARED_READ.has(folder) ? [TEAM_READ_ALL_ENTRY] : []),
    ...(clear ? [clear] : []),
    ...(folder === 'scheduled'
      ? [{ ...SEND_ALL_ENTRY, comingSoon: true }]
      : folder === 'spam'
        ? [DELETE_ALL_ENTRY]
        : []),
  ];

  return railRowMenu({
    row,
    itemNoun: 'email',
    containerNoun: 'folder',
    readable: !TEAM_UNREADABLE.has(folder),
    live,
    refused: context.purgeRefused ? { 'empty-trash': context.purgeRefused } : undefined,
    extras,
  });
}

/**
 * The Team Inbox chevron's menu. The level is the member's OWN for the team mailbox (the prototype: "your personal
 * Team Inbox setting"); the team-wide setting stays on the Mail settings page, for the Owner and Admins.
 * `current` is `undefined` only before it has ever been loaded: nothing is ticked then, rather than a guessed "Anyone".
 */
export function teamMailVolumeMenu(current: MailVolume | undefined): readonly RailMenuItem[] {
  return [
    ...MAIL_VOLUME_GROUPS.map(({ value, title, icon, hint }) => ({
      id: MAIL_VOLUME_ENTRY_PREFIX + value,
      title,
      icon,
      hint,
      checked: current !== undefined && mailVolumeChecked(current, value),
    })),
    null,
    { id: 'open-other', title: 'Open Other Mail', icon: 'sapphire-email' },
    null,
    { id: 'read-all', title: 'Mark all as read for me', icon: 'sapphire-email', comingSoon: true },
    TEAM_READ_ALL_ENTRY,
    { id: 'volume-settings', title: 'Mail volume settings…', icon: 'sapphire-settings' },
  ];
}
